import {
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Button,
  Paper,
  Divider,
  Box,
  Avatar,
} from '@mui/material'
import mapping from 'atoms/Icon/mapping'
import Loading from 'atoms/Loading'
import { IntegrationModule } from 'common/types/kraken-core/IntegrationModule'
import JsonForm from 'molecules/JsonForm/JsonForm'
import { useEffect, useState } from 'react'
import { getIntegrationModule } from 'services/kraken-core/integration_module/integration.service'

export interface IntegrationModuleInstallationWizardProps {
  integrationModuleId: string
  onClose: () => void
  onSuccess: () => void
  onError: (error: string) => void
}

export default function IntegrationModuleInstallationWizard({
  integrationModuleId,
  onClose,
  onSuccess,
  onError,
}: IntegrationModuleInstallationWizardProps) {
  const [integrationModule, setIntegrationModule] = useState<IntegrationModule | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [activeStep, setActiveStep] = useState<number>(0)

  const steps = ['Parameters', 'Confirm', 'Finish']

  const handleNext = () => {
    setActiveStep(prevStep => Math.min(prevStep + 1, steps.length - 1))
  }

  const handleBack = () => {
    setActiveStep(prevStep => Math.max(prevStep - 1, 0))
  }

  useEffect(() => {
    setIsLoading(true)
    getIntegrationModule(integrationModuleId)
      .then(response => {
        if (response.response) {
          setIntegrationModule(response.response.data)
        } else {
          onError('Failed to fetch integration module')
        }
      })
      .catch(error => {
        onError('Failed to fetch integration module')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [integrationModuleId])

  return (
    <Paper
      sx={{
        position: 'relative',
        minWidth: '80%',
        minHeight: '80%',
        maxWidth: '80%',
        maxHeight: '80%',
        overflow: 'hidden',
        boxShadow: 24,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isLoading && (
        <Box p={2}>
          <Loading />
        </Box>
      )}
      {!isLoading && !integrationModule && (
        <Box p={2}>
          <p>Integration module not found</p>
        </Box>
      )}

      {!isLoading && integrationModule ? (
        <>
          <Box
            flexDirection={'row'}
            display="flex"
            alignItems="center"
            sx={{
              p: 2,
            }}
          >
            <Avatar
              sizes="large"
              sx={{
                backgroundColor: 'secondary.main',
              }}
            >
              {mapping['IntegrationModule']}
            </Avatar>
            <Stack sx={{ pl: 2 }}>
              <Typography variant="caption" fontWeight={'bold'}>
                Installation
              </Typography>
              <Typography variant="h2" fontWeight={'bold'} sx={{ flexShrink: 0 }}>
                {integrationModule?.name || 'Integration Module'}
              </Typography>
            </Stack>
          </Box>

          <Divider />

          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{ width: '100%', pt: 2, flexShrink: 0 }}
          >
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Stack sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {!isLoading && integrationModule && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <JsonForm
                  schema={integrationModule?.metadata.input_schema}
                  data={{}}
                  onChange={data => {
                    console.log('Form data changed:', data)
                  }}
                />
              </Paper>
            )}
          </Stack>
          <Divider />
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'white',
              p: 2,
              flexShrink: 0,
            }}
          >
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Button variant="contained" disabled={activeStep === 0} onClick={handleBack}>
                Previous
              </Button>
              <Button
                variant="contained"
                disabled={activeStep === steps.length - 1}
                onClick={handleNext}
              >
                Next
              </Button>
            </Stack>
          </Box>
        </>
      ) : null}
    </Paper>
  )
}
